import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink } from "lucide-react";
import { slugify } from "@/lib/articleText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { title: "", slug: "", excerpt: "", body: "", published: false };

const inputCls =
  "w-full border border-white/10 bg-[#0B0F17] px-4 py-3 text-sm font-light text-slate-200 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-[#F26122]/60";

const AdminArticles = ({ token }) => {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/articles`, { headers });
      setArticles(data.articles);
    } catch {
      toast.error("Could not load articles");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setForm(emptyForm);
    setSlugTouched(false);
    setEditing("new");
  };

  const startEdit = (a) => {
    setForm({
      title: a.title,
      slug: a.slug,
      excerpt: a.excerpt || "",
      body: a.body || "",
      published: !!a.published,
    });
    setSlugTouched(true);
    setEditing(a.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      if (editing === "new") {
        await axios.post(`${API}/admin/articles`, payload, { headers });
        toast.success("Article created");
      } else {
        await axios.put(`${API}/admin/articles/${editing}`, payload, { headers });
        toast.success("Article updated");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error("Save failed", {
        description:
          typeof err.response?.data?.detail === "string"
            ? err.response.data.detail
            : "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, { headers });
      toast.success("Article deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (editing) {
    return (
      <form
        onSubmit={save}
        data-testid="article-editor-form"
        className="mt-10 border border-white/10 bg-[#0D1220] p-6 sm:p-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-light text-slate-100">
            {editing === "new" ? "New article" : "Edit article"}
          </h2>
          <button
            type="button"
            data-testid="article-form-cancel"
            onClick={() => setEditing(null)}
            className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs text-slate-300 transition-colors hover:border-red-400/60 hover:text-red-400"
          >
            <X className="h-3.5 w-3.5" /> Cancel
          </button>
        </div>

        <div className="mt-8 space-y-5">
          <div>
            <label className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Title
            </label>
            <input
              data-testid="article-form-title"
              required
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  title: e.target.value,
                  slug: slugTouched ? f.slug : slugify(e.target.value),
                }))
              }
              placeholder="e.g. Bank Debt vs NBFC vs Private Credit"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Web address (slug)
            </label>
            <input
              data-testid="article-form-slug"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setForm((f) => ({ ...f, slug: e.target.value }));
              }}
              placeholder="auto-generated-from-title"
              className={inputCls}
            />
            <p className="mt-1.5 text-xs font-light text-slate-600">
              The article will live at /insights/{form.slug || "your-title"}
            </p>
          </div>
          <div>
            <label className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Summary (shown on the card)
            </label>
            <textarea
              data-testid="article-form-excerpt"
              rows={2}
              value={form.excerpt}
              onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              placeholder="One or two sentences that appear on the Insights card"
              className={`${inputCls} resize-none`}
            />
          </div>
          <div>
            <label className="mb-2 block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Article body
            </label>
            <textarea
              data-testid="article-form-body"
              rows={16}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder={"Write the article here.\n\n## This becomes a chapter heading\n\n- This becomes a bullet point\n\n> This becomes a highlighted note\n\nEverything else is a paragraph. Separate paragraphs with a blank line."}
              className={`${inputCls} resize-y font-mono2 leading-relaxed`}
            />
            <p className="mt-1.5 text-xs font-light text-slate-600">
              Start a line with ## for a chapter heading, - for a bullet, &gt;
              for a note. Blank line = new paragraph.
            </p>
          </div>
          <label className="flex cursor-pointer items-center gap-3">
            <input
              data-testid="article-form-published"
              type="checkbox"
              checked={form.published}
              onChange={(e) =>
                setForm((f) => ({ ...f, published: e.target.checked }))
              }
              className="h-4 w-4 accent-[#F26122]"
            />
            <span className="text-sm font-light text-slate-300">
              Published — visible on the website
            </span>
          </label>
        </div>

        <button
          data-testid="article-form-save"
          type="submit"
          disabled={saving}
          className="mt-8 bg-[#F26122] px-8 py-3.5 text-sm font-medium text-[#0B0F17] transition-colors duration-300 hover:bg-[#FF7B3D] disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Article"}
        </button>
      </form>
    );
  }

  return (
    <div className="mt-10">
      <button
        data-testid="article-new-button"
        onClick={startNew}
        className="flex items-center gap-2 bg-[#F26122] px-5 py-3 text-sm font-medium text-[#0B0F17] transition-colors duration-300 hover:bg-[#FF7B3D]"
      >
        <Plus className="h-4 w-4" /> New Article
      </button>

      {!articles ? (
        <p className="mt-16 text-center text-sm text-slate-500">Loading…</p>
      ) : articles.length === 0 ? (
        <p
          data-testid="articles-empty"
          className="mt-16 text-center text-sm text-slate-500"
        >
          No articles yet.
        </p>
      ) : (
        <div
          data-testid="admin-articles-list"
          className="mt-6 space-y-px border border-white/10 bg-white/10"
        >
          {articles.map((a) => (
            <article
              key={a.id}
              data-testid={`article-item-${a.id}`}
              className="flex flex-wrap items-center justify-between gap-4 bg-[#0D1220] px-6 py-5"
            >
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="font-display text-lg text-slate-100">
                    {a.title}
                  </h2>
                  <span
                    className={`px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-[0.2em] ${
                      a.published
                        ? "bg-[#F26122]/15 text-[#F2824F]"
                        : "bg-white/5 text-slate-500"
                    }`}
                  >
                    {a.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="mt-1 font-mono2 text-[10px] tracking-[0.15em] text-slate-600">
                  /insights/{a.slug}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {a.published && (
                  <a
                    data-testid={`article-view-${a.id}`}
                    href={`/insights/${a.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-slate-400 transition-colors hover:border-[#F26122]/60 hover:text-[#F26122]"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> View
                  </a>
                )}
                <button
                  data-testid={`article-edit-${a.id}`}
                  onClick={() => startEdit(a)}
                  className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-slate-300 transition-colors hover:border-[#F26122]/60 hover:text-[#F26122]"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  data-testid={`article-delete-${a.id}`}
                  onClick={() => remove(a)}
                  className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs text-slate-300 transition-colors hover:border-red-400/60 hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminArticles;
